// src/pages/cards/CardsInitialValidation.jsx

import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
const API = String(process.env.REACT_APP_API || "").replace(/\/$/, "");

const MESSAGE = "Valida tu inicial para activar tu garantía.";

const buildSpokenInstruction = (initialAmount) => {
    const amount = Number(initialAmount || 0);
    const formatted = Number.isFinite(amount) ? amount.toFixed(0) : "0";
    return `Brindé una inicial de ${formatted} soles.`;
};

const onlyDigits = (value = "", max = 99) =>
    String(value || "").replace(/\D/g, "").slice(0, max);

const normalizeToken = (value = "") =>
    String(value || "")
        .trim()
        .replace(/[^a-zA-Z0-9_-]/g, "")
        .slice(0, 100);

const getPublicJson = async (url, options = {}) => {
    const response = await fetch(url, {
        ...options,
        credentials: "omit",
        cache: "no-store",
        headers: {
            Accept: "application/json",
            ...(options.body ? { "Content-Type": "application/json" } : {}),
            ...(options.headers || {}),
        },
    });

    let payload = null;
    try {
        payload = await response.json();
    } catch (_) {
        payload = null;
    }

    if (!response.ok) {
        const error = new Error(payload?.msg || payload?.error || `Error HTTP ${response.status}`);
        error.status = response.status;
        error.payload = payload;
        throw error;
    }

    return payload;
};

const getTokenFromUrl = () => {
    const parts = String(window.location.pathname || "").split("/").filter(Boolean);

    if (parts[0] === "cards-initial-validation" && parts[1]) {
        return normalizeToken(decodeURIComponent(parts[1]));
    }

    const params = new URLSearchParams(window.location.search);
    return normalizeToken(params.get("token") || "");
};

export default function CardsInitialValidation() {
    const token = useMemo(getTokenFromUrl, []);
    const params = useMemo(() => new URLSearchParams(window.location.search), []);
    const dniFromUrl = onlyDigits(params.get("dni") || "", 9);

    const videoRef = useRef(null);
    const previewRef = useRef(null);
    const streamRef = useRef(null);
    const recorderRef = useRef(null);
    const chunksRef = useRef([]);
    const autoStopRef = useRef(null);
    const intervalRef = useRef(null);

    const [session, setSession] = useState(null);
    const [sessionLoading, setSessionLoading] = useState(true);
    const [cameraReady, setCameraReady] = useState(false);
    const [instructionListened, setInstructionListened] = useState(false);
    const [instructionPlaying, setInstructionPlaying] = useState(false);
    const [recording, setRecording] = useState(false);
    const [countdown, setCountdown] = useState(5);
    const [videoBlob, setVideoBlob] = useState(null);
    const [videoUrl, setVideoUrl] = useState("");
    const [message, setMessage] = useState("");
    const [done, setDone] = useState(false);
    const [sending, setSending] = useState(false);
    const sendingRef = useRef(false);

    const stopCamera = useCallback(() => {
        streamRef.current?.getTracks?.().forEach((track) => track.stop());
        streamRef.current = null;
        setCameraReady(false);
    }, []);

    const speakInstruction = useCallback(() => {
        return new Promise((resolve) => {
            try {
                if (!window.speechSynthesis) {
                    resolve();
                    return;
                }

                window.speechSynthesis.cancel();
                setInstructionPlaying(true);

                const utter = new SpeechSynthesisUtterance(
                    buildSpokenInstruction(session?.initialAmount),
                );
                utter.lang = "es-PE";
                utter.rate = 0.92;
                utter.onend = () => {
                    setInstructionPlaying(false);
                    resolve();
                };
                utter.onerror = () => {
                    setInstructionPlaying(false);
                    resolve();
                };

                window.speechSynthesis.speak(utter);
            } catch (_) {
                setInstructionPlaying(false);
                resolve();
            }
        });
    }, [session?.initialAmount]);

    const startCamera = useCallback(async () => {
        try {
            stopCamera();

            const stream = await navigator.mediaDevices.getUserMedia({
                video: {
                    facingMode: "user",
                    width: { ideal: 960 },
                    height: { ideal: 540 },
                },
                audio: true,
            });

            streamRef.current = stream;

            if (videoRef.current) {
                videoRef.current.srcObject = stream;
                await videoRef.current.play();
            }

            setCameraReady(true);
            setMessage("");
        } catch (error) {
            console.error("cards initial validation camera error:", error);
            setCameraReady(false);
            setMessage("No se pudo abrir la cámara o micrófono. Revisa permisos del navegador y usa HTTPS.");
        }
    }, [stopCamera]);

    const loadSession = useCallback(async () => {
        if (!token) {
            setSessionLoading(false);
            setMessage("Enlace inválido. Falta token de validación.");
            return;
        }

        try {
            setSessionLoading(true);

            const cacheBust = Date.now();
            const data = await getPublicJson(`${API}/cards-initial-validation-open/${encodeURIComponent(token)}?_=${cacheBust}`);

            const payload = data?.data || null;

            if (!payload) {
                throw new Error("No se pudo cargar la validación.");
            }

            if (dniFromUrl && payload.dni && String(payload.dni) !== String(dniFromUrl)) {
                throw new Error("El DNI del enlace no coincide con la validación.");
            }

            setSession(payload);
            setDone(Boolean(payload.completed));

            if (payload.completed) {
                setMessage("Validación de inicial completada. Ya puedes regresar con el asesor.");
                stopCamera();
            } else {
                // IMPORTANTE MOBILE/iPhone:
                // No iniciar voz ni cámara automáticamente al cargar la página.
                // Safari/Chrome móvil exigen una acción directa del usuario para audio/cámara.
                setMessage("Presiona Escuchar indicación para activar la validación.");
                setInstructionListened(false);
            }
        } catch (error) {
            setMessage(error?.payload?.msg || error?.payload?.error || error?.message || "No se pudo validar el enlace.");
            stopCamera();
        } finally {
            setSessionLoading(false);
        }
    }, [dniFromUrl, speakInstruction, startCamera, stopCamera, token]);

    useEffect(() => {
        loadSession();

        return () => {
            stopCamera();

            if (autoStopRef.current) clearTimeout(autoStopRef.current);
            if (intervalRef.current) clearInterval(intervalRef.current);
            if (videoUrl) URL.revokeObjectURL(videoUrl);
        };
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    const startRecording = () => {
        const stream = streamRef.current;

        if (!instructionListened) {
            setMessage("Primero debes escuchar la indicación completa antes de grabar.");
            return;
        }

        if (!stream || recording) return;

        chunksRef.current = [];

        const mimeType = MediaRecorder.isTypeSupported("video/webm;codecs=vp8,opus")
            ? "video/webm;codecs=vp8,opus"
            : "video/webm";

        const recorder = new MediaRecorder(stream, {
            mimeType,
            videoBitsPerSecond: 650000,
            audioBitsPerSecond: 64000,
        });

        recorderRef.current = recorder;

        recorder.ondataavailable = (event) => {
            if (event.data && event.data.size > 0) {
                chunksRef.current.push(event.data);
            }
        };

        recorder.onstop = () => {
            const blob = new Blob(chunksRef.current, {
                type: "video/webm",
            });

            if (videoUrl) URL.revokeObjectURL(videoUrl);

            const url = URL.createObjectURL(blob);

            setVideoBlob(blob);
            setVideoUrl(url);
            setRecording(false);
            setCountdown(5);
            setMessage("Video grabado. Revísalo y confirma el envío si está correcto.");
        };

        setVideoBlob(null);
        setVideoUrl("");
        setRecording(true);
        setCountdown(5);

        recorder.start(250);

        let left = 5;

        intervalRef.current = setInterval(() => {
            left -= 1;
            setCountdown(Math.max(left, 0));

            if (left <= 0 && intervalRef.current) {
                clearInterval(intervalRef.current);
            }
        }, 1000);

        autoStopRef.current = setTimeout(() => {
            if (recorder.state !== "inactive") {
                recorder.stop();
            }
        }, 5000);
    };

    const retake = async () => {
        if (videoUrl) URL.revokeObjectURL(videoUrl);

        setVideoBlob(null);
        setVideoUrl("");
        setMessage("Vuelve a grabar mirando a la cámara. La cámara ya está lista.");

        await startCamera();
    };

    const submit = async () => {
        if (sendingRef.current) return;

        if (!videoBlob || !session?.dni) {
            setMessage("Primero graba el video de validación.");
            return;
        }

        sendingRef.current = true;
        setSending(true);

        try {
            const reader = new FileReader();

            const dataUrl = await new Promise((resolve, reject) => {
                reader.onloadend = () => resolve(String(reader.result || ""));
                reader.onerror = reject;
                reader.readAsDataURL(videoBlob);
            });

            const response = await getPublicJson(`${API}/cards-initial-validation-open/submit`, {
                method: "POST",
                body: JSON.stringify({
                    token,
                    dni: session.dni,
                    video: dataUrl,
                }),
            });

            if (response?.msg !== "success") {
                throw new Error(response?.msg || "No se pudo enviar la validación.");
            }

            setDone(true);
            setMessage("Validación enviada correctamente. Regresa con el asesor para verificar.");
            stopCamera();
        } catch (error) {
            setMessage(error?.payload?.msg || error?.payload?.error || error?.message || "No se pudo enviar la validación.");
        } finally {
            sendingRef.current = false;
            setSending(false);
        }
    };

    if (sessionLoading) {
        return (
            <main className="civPage">
                <section className="civCard">
                    <h1>Validando enlace...</h1>
                    <p>Espere un momento.</p>
                    <Styles />
                </section>
            </main>
        );
    }

    return (
        <main className="civPage">
            <Styles />

            <section className="civCard">
                <div className="civHeader">
                    <h1>{MESSAGE}</h1>

                    {session?.dni && (
                        <p>
                            Documento {session.dni} · Inicial S/{" "}
                            {Number(session.initialAmount || 0).toFixed(2)}
                        </p>
                    )}
                </div>

                {done ? (
                    <div className="civDone">
                        <h2>✅ Validación completada</h2>
                        <p>{message || "Ya puedes regresar con el asesor."}</p>
                    </div>
                ) : (
                    <>
                        <div className="civInstruction">
                            <b>Mensaje que debes decir:</b>

                            <p>
                                “Brindé una inicial de{" "}
                                {Number(session?.initialAmount || 0).toFixed(0)} soles.”
                            </p>

                            <button
                                type="button"
                                onClick={async () => {
                                    setMessage("Escucha la indicación completa. Luego se activará la cámara.");
                                    setInstructionListened(false);
                                    await speakInstruction();
                                    setInstructionListened(true);
                                    await startCamera();
                                }}
                                disabled={instructionPlaying || recording}
                            >
                                {instructionPlaying ? "Reproduciendo indicación..." : "Escuchar indicación"}
                            </button>
                        </div>

                        {!videoUrl ? (
                            <div className="civCameraBox">
                                <video ref={videoRef} playsInline muted autoPlay className="civVideo" />
                                <div className="civFaceFrame" />

                                {recording && <div className="civCountdown">{countdown}</div>}
                            </div>
                        ) : (
                            <div className="civCameraBox">
                                <video
                                    ref={previewRef}
                                    src={videoUrl}
                                    controls
                                    playsInline
                                    className="civVideo"
                                />
                            </div>
                        )}

                        <div className="civActions">
                            {!videoUrl ? (
                                <button
                                    type="button"
                                    className="civPrimary"
                                    onClick={startRecording}
                                    disabled={!instructionListened || !cameraReady || recording}
                                >
                                    {recording
                                        ? `Grabando ${countdown}s`
                                        : instructionListened
                                            ? "Grabar video 5 segundos"
                                            : "Escucha primero la indicación"}
                                </button>
                            ) : (
                                <>
                                    <button
                                        type="button"
                                        className="civGhost"
                                        onClick={retake}
                                        disabled={sending}
                                    >
                                        Volver a grabar
                                    </button>

                                    <button
                                        type="button"
                                        className="civPrimary"
                                        onClick={submit}
                                        disabled={sending}
                                    >
                                        {sending ? "Enviando..." : "Confirmar y enviar"}
                                    </button>
                                </>
                            )}
                        </div>
                    </>
                )}

                {message && <div className="civNotice">{message}</div>}
            </section>
        </main>
    );
}

function Styles() {
    return (
        <style>{`
      .civPage {
        min-height: 100vh;
        background: linear-gradient(145deg, #4d0084, #12001f);
        padding: 22px;
        display: flex;
        align-items: center;
        justify-content: center;
        font-family: Inter, Arial, sans-serif;
      }

      .civCard {
        width: min(560px, 100%);
        background: #fff;
        border-radius: 24px;
        padding: 20px;
        box-shadow: 0 24px 70px rgba(0, 0, 0, .32);
      }

      .civHeader {
        text-align: center;
        margin-bottom: 16px;
      }

      .civHeader span {
        display: inline-flex;
        background: #4d0084;
        color: #fff;
        border-radius: 999px;
        padding: 6px 12px;
        font-size: 12px;
        font-weight: 900;
      }

      .civHeader h1 {
        font-size: 23px;
        margin: 12px 0 6px;
        color: #111827;
      }

      .civHeader p {
        margin: 0;
        color: #64748b;
        font-weight: 700;
      }

      .civInstruction {
        background: #f8fafc;
        border: 1px solid #e2e8f0;
        border-radius: 18px;
        padding: 14px;
        margin-bottom: 14px;
      }

      .civInstruction b {
        color: #111827;
      }

      .civInstruction p {
        margin: 8px 0 10px;
        color: #334155;
        line-height: 1.45;
      }

      .civInstruction button,
      .civGhost {
        border: 1px solid #cbd5e1;
        background: #fff;
        color: #111827;
        border-radius: 12px;
        padding: 11px 14px;
        font-weight: 800;
      }

      .civCameraBox {
        position: relative;
        overflow: hidden;
        border-radius: 20px;
        background: #020617;
        aspect-ratio: 3 / 4;
        display: flex;
        align-items: center;
        justify-content: center;
      }

      .civVideo {
        width: 100%;
        height: 100%;
        object-fit: cover;
      }

      .civFaceFrame {
        position: absolute;
        width: 95%;
        height: 75%;
        border: 3px solid rgba(255, 255, 255, .9);
        border-radius: 50%;
        box-shadow: 0 0 0 999px rgba(0, 0, 0, .24);
      }

      .civCountdown {
        position: absolute;
        inset: auto auto 18px 50%;
        transform: translateX(-50%);
        width: 58px;
        height: 58px;
        border-radius: 999px;
        background: #4d0084;
        color: #fff;
        display: grid;
        place-items: center;
        font-size: 26px;
        font-weight: 900;
      }

      .civActions {
        display: flex;
        gap: 10px;
        justify-content: center;
        flex-wrap: wrap;
        margin-top: 16px;
      }

      .civPrimary {
        border: 0;
        background: #4d0084;
        color: #fff;
        border-radius: 14px;
        padding: 13px 18px;
        font-weight: 900;
        box-shadow: 0 12px 24px rgba(77, 0, 132, .24);
      }

      button:disabled {
        opacity: .55;
        cursor: not-allowed;
      }

      .civNotice {
        margin-top: 14px;
        background: #f1f5f9;
        color: #334155;
        border-radius: 14px;
        padding: 12px;
        text-align: center;
        font-weight: 700;
      }

      .civDone {
        background: #ecfdf5;
        border: 1px solid #bbf7d0;
        border-radius: 18px;
        padding: 18px;
        text-align: center;
      }

      .civDone h2 {
        margin: 0 0 8px;
        color: #047857;
      }

      .civDone p {
        margin: 0;
        color: #065f46;
      }
    `}</style>
    );
}
